import { Button, Dropdown, DropdownItem, Icon, Separator, cls } from "@dgs/core";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ContactViewIndexResource } from "../../shared/types";
import { useGlobalContactView } from "./GlobalContactViewProvider";

const DropdownIcon = styled(Icon)`
	width: 16px;
	height: 16px;
	transform: rotate(90deg);
`;

const View = styled.div`
	display: flex;
	align-items: center;
	height: 100%;
	border-right: 1px solid ${({ theme }) => theme.colors.palette.grey[30]};
	padding-right: 8px;
`;

const StyledButton = styled(Button)`
	> * {
		display: grid;
		grid-template-columns: max-content max-content max-content;
		margin-left: -8px;
		margin-right: -8px;
	}
`;

const ContactViewIcon = styled(Icon)`
	width: 20px;
	height: 20px;
`;

const SeparatorWrapper = styled.div`
	display: grid;
	grid-template-columns: 1fr max-content 1fr;
	gap: 8px;
	padding: 8px 0;
	align-items: center;
	font-size: ${({ theme }) => theme.fonts.sizes.small};
	color: ${({ theme }) => theme.colors.palette.grey[70]};
`;

export const GlobalContactViewSwitcher: FC = () => {
	const { t } = useTranslation();
	const { contactViews, favoriteContactViews, setGlobalContactView, globalContactView } = useGlobalContactView();
	const title = t("Contact view");

	const handleClick = (contactView: ContactViewIndexResource) => {
		setGlobalContactView(contactView);
	};

	if (contactViews.length + favoriteContactViews.length === 0 || !globalContactView) return null;

	return (
		<Dropdown
			renderTrigger={(props) => (
				<StyledButton title={title} prefix={<ContactViewIcon title={title} $icon="layers" />} {...props}>
					<View>{globalContactView ? globalContactView.name : t("Select a contact view")}</View>
					<DropdownIcon $icon="caret" title={title} />
				</StyledButton>
			)}
			renderContent={(handleClose) => (
				<>
					<ItemsWithSeparator
						guestViews={favoriteContactViews}
						separatorLabel={t("Favorites")}
						onClick={(contactView) => {
							handleClick(contactView);
							handleClose();
						}}
						currentGuestView={globalContactView}
					/>
					<ItemsWithSeparator
						guestViews={contactViews}
						separatorLabel={t("All")}
						onClick={(contactView) => {
							handleClick(contactView);
							handleClose();
						}}
						currentGuestView={globalContactView}
					/>
				</>
			)}
		/>
	);
};

interface ItemsWithSeparator {
	currentGuestView: ContactViewIndexResource | null;
	separatorLabel: string;
	guestViews: ContactViewIndexResource[];
	onClick: (contactView: ContactViewIndexResource) => void;
}

const ItemsWithSeparator: FC<ItemsWithSeparator> = ({ separatorLabel, guestViews, currentGuestView, onClick }) => {
	if (guestViews.length === 0) {
		return null;
	}

	return (
		<>
			<SeparatorWrapper>
				<Separator />
				<span>{separatorLabel}</span>
				<Separator />
			</SeparatorWrapper>
			{guestViews.map((guestView, index) => (
				<DropdownItem
					className={cls(guestViews.findIndex((view) => view.id === currentGuestView?.id) === index && "selected")}
					onClick={() => onClick(guestView)}
					key={index}
				>
					{guestView.name}
				</DropdownItem>
			))}
		</>
	);
};
